/**
 * Хук для работы с Telegram WebApp API.
 */
import { useEffect, useCallback } from "react"

function isTelegramMethodUnsupported(err: unknown): boolean {
  if (!err) return false
  const message = err instanceof Error ? err.message : String(err)
  return message.includes("WebAppMethodUnsupported") || message.includes("WebAppVersionUnsupported")
}

function safeTelegramCall(action: () => void, fallback?: () => void) {
  try {
    action()
  } catch (err) {
    if (!isTelegramMethodUnsupported(err)) {
      console.warn("Telegram WebApp method failed", err)
    }
    fallback?.()
  }
}

function schemeFromTelegramBackground(value?: string): "light" | "dark" | null {
  const match = /^#([0-9a-f]{6})$/i.exec(value?.trim() || "")
  if (!match) return null

  const color = Number.parseInt(match[1], 16)
  const red = (color >> 16) & 0xff
  const green = (color >> 8) & 0xff
  const blue = color & 0xff
  const brightness = (red * 299 + green * 587 + blue * 114) / 1000
  return brightness < 160 ? "dark" : "light"
}

export function resolveTelegramColorScheme(tg?: TelegramWebApp): "light" | "dark" {
  // The Telegram SDK also exists when the Vite URL is opened in a normal browser
  // and reports its own default light scheme there. Only a real initData session
  // is allowed to select the runtime theme; browser/early runtime stays on Figma Dark.
  if (!tg?.initData) return "dark"

  const backgroundScheme = schemeFromTelegramBackground(tg?.themeParams?.bg_color)
  if (backgroundScheme) return backgroundScheme
  if (tg?.colorScheme === "light" || tg?.colorScheme === "dark") return tg.colorScheme
  return "dark"
}

export function useTelegram() {
  const tg = window.Telegram?.WebApp

  useEffect(() => {
    if (tg) {
      safeTelegramCall(() => tg.ready())
      if (tg.exitFullscreen && tg.isFullscreen) {
        safeTelegramCall(() => tg.exitFullscreen?.())
      }
      safeTelegramCall(() => tg.expand())
    }
  }, [tg])

  const showAlert = useCallback(
    (message: string) => {
      if (tg?.showAlert) {
        safeTelegramCall(() => tg.showAlert(message), () => window.alert(message))
      } else {
        window.alert(message)
      }
    },
    [tg]
  )

  const showConfirm = useCallback(
    (message: string): Promise<boolean> => {
      return new Promise((resolve) => {
        if (tg?.showConfirm) {
          safeTelegramCall(
            () => tg.showConfirm(message, resolve),
            () => resolve(window.confirm(message))
          )
        } else {
          resolve(window.confirm(message))
        }
      })
    },
    [tg]
  )

  const hapticImpact = useCallback(
    (style: "light" | "medium" | "heavy" = "medium") => {
      if (!tg?.HapticFeedback?.impactOccurred) return
      safeTelegramCall(() => tg.HapticFeedback.impactOccurred(style))
    },
    [tg]
  )

  const hapticNotification = useCallback(
    (type: "success" | "error" | "warning") => {
      if (!tg?.HapticFeedback?.notificationOccurred) return
      safeTelegramCall(() => tg.HapticFeedback.notificationOccurred(type))
    },
    [tg]
  )

  const close = useCallback(() => {
    if (!tg?.close) return
    safeTelegramCall(() => tg.close())
  }, [tg])

  const openLink = useCallback(
    (url: string) => {
      if (tg?.openLink) {
        safeTelegramCall(() => tg.openLink(url), () => window.open(url, "_blank")?.focus())
      } else {
        window.open(url, "_blank")?.focus()
      }
    },
    [tg]
  )

  const shareUrl = useCallback(
    (url: string, text?: string) => {
      const shareText = text ? `${text}\n${url}` : url
      const telegramShareUrl = `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text || "")}`

      const browserFallback = () => {
        if (navigator.share) {
          navigator.share({ url, text }).catch(() => {
            navigator.clipboard?.writeText(shareText)
          })
        } else {
          navigator.clipboard?.writeText(shareText)
        }
      }

      if (tg?.openTelegramLink) {
        safeTelegramCall(() => tg.openTelegramLink(telegramShareUrl), browserFallback)
      } else {
        browserFallback()
      }
    },
    [tg]
  )

  const initData = tg?.initData || ""
  const user = tg?.initDataUnsafe?.user
  const startParam = tg?.initDataUnsafe?.start_param || new URLSearchParams(window.location.search).get("startapp") || undefined
  const colorScheme = resolveTelegramColorScheme(tg)
  const isDark = colorScheme === "dark"

  return {
    tg,
    initData,
    user,
    startParam,
    colorScheme,
    isDark,
    showAlert,
    showConfirm,
    hapticImpact,
    hapticNotification,
    close,
    openLink,
    shareUrl,
  }
}
